import React, { forwardRef, useState, useCallback } from 'react';
import { TextField, TextFieldProps, InputAdornment, Tooltip } from '@mui/material';
import { IMaskInput, IMask } from 'react-imask';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

// ─── Tipos ────────────────────────────────────────────────────────────────────

interface DurationValue {
    raw: string;       // '14:30' (HH:MM)
    totalMinutes: number;
    hours: number;
    minutes: number;
    isValid: boolean;
}

export interface DurationInputProps
    extends Omit<TextFieldProps, 'value' | 'onChange'> {
    value?: string;                         // formato 'HH:MM'
    onChange?: (duration: DurationValue) => void;
    min?: string;                           // default '00:01'
    max?: string;                           // default '24:00'
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const parseTime = (raw: string): { hours: number; minutes: number } | null => {
    const match = raw.match(/^(\d{1,2}):(\d{2})$/);
    if (!match) return null;
    return { hours: parseInt(match[1], 10), minutes: parseInt(match[2], 10) };
};

const toMinutes = (raw: string): number | null => {
    const parsed = parseTime(raw);
    if (!parsed) return null;
    return parsed.hours * 60 + parsed.minutes;
};

const MASK_COMPLETE_LENGTH = 5; // 'HH:MM'

const buildDurationValue = ( raw: string, minStr: string, maxStr: string ): DurationValue => {
    const isComplete = raw.length === MASK_COMPLETE_LENGTH && !raw.includes('_');
    const totalMinutes = toMinutes(raw) ?? 0;
    const minMinutes = toMinutes(minStr) ?? 1;
    const maxMinutes = toMinutes(maxStr) ?? 1440;
    const parsed = parseTime(raw);

    const isValid =
        isComplete &&
        parsed !== null &&
        parsed.minutes <= 59 &&
        totalMinutes >= minMinutes &&
        totalMinutes <= maxMinutes;

    return {
        raw,
        totalMinutes,
        hours: parsed?.hours ?? 0,
        minutes: parsed?.minutes ?? 0,
        isValid,
    };
};

// ─── Adapter IMask → MUI ──────────────────────────────────────────────────────

interface MaskAdapterProps {
    onChange: (event: { target: { value: string } }) => void;
    name?: string;
}

/**
 * Adapter que conecta IMaskInput con el inputComponent de MUI TextField.
 * forwardRef es necesario porque MUI pasa la ref al input interno.
 */
const DurationMaskAdapter = forwardRef<HTMLInputElement, MaskAdapterProps>(
    function DurationMaskAdapter({ onChange, ...rest }, ref) {
        return (
            <IMaskInput
                {...rest}
                inputRef={ref}
                // Patrón: dos dígitos para hora, dos para minutos
                mask="HH:MM"
                blocks={{
                    HH: {
                        mask: IMask.MaskedRange,
                        from: 0,
                        to: 24,
                        maxLength: 2,
                        autofix: 'pad',    // rellena con 0 a la izquierda al completar
                    },
                    MM: {
                        mask: IMask.MaskedRange,
                        from: 0,
                        to: 59,
                        maxLength: 2,
                        autofix: 'pad',
                    },
                }}
                /**
                 * validate permite rechazar combinaciones inválidas a nivel de máscara.
                 * Bloquea cualquier valor > '24:00', ej: '24:01' ... '24:59'
                 */
                validate={(value: string) => {
                    const parsed = parseTime(value);
                    if (!parsed) return true; // incompleto, dejar pasar
                    if (parsed.hours === 24 && parsed.minutes > 0) return false;
                    return true;
                }}
                lazy={false}             // muestra el placeholder de máscara siempre
                placeholderChar="_"
                overwrite
                onAccept={(val: string) => onChange({ target: { value: val as string } })}
            />
        );
    },
);

// ─── Componente principal ─────────────────────────────────────────────────────

export const DurationInput: React.FC<DurationInputProps> = ({
    value = '',
    onChange,
    min = '00:01',
    max = '24:00',
    label = 'Duración',
    helperText,
    error,
    ...textFieldProps
}) => {
    const [touched, setTouched] = useState(false);

    const handleChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const raw = event.target.value;
            const duration = buildDurationValue(raw, min, max);
            onChange?.(duration);
        },
        [onChange, min, max],
    );

    const handleBlur = useCallback(( e: React.FocusEvent<HTMLInputElement> ) => {
            setTouched(true);
            textFieldProps.onBlur?.(e);
        },
        [textFieldProps],
    );

    // Calcular error interno solo tras interacción (touched)
    const duration = buildDurationValue(value, min, max);
    const isComplete = value.length === MASK_COMPLETE_LENGTH && !value.includes('_');
    const hasInternalError = touched && isComplete && !duration.isValid;

    // Mensaje de error dinámico
    const getErrorMessage = (): string => {
        if (!isComplete) return '';
        const total = duration.totalMinutes;
        const minTotal = toMinutes(min) ?? 1;
        const maxTotal = toMinutes(max) ?? 1440;
        if (total < minTotal) return `Mínimo ${min}`;
        if (total > maxTotal) return `Máximo ${max}`;
        return 'Valor inválido';
    };

    const showError = error ?? hasInternalError;
    const displayHelper = helperText ?? (showError ? getErrorMessage() : '');

    return (
        <Tooltip
            title={`Rango válido: ${min} – ${max}`}
            placement="top"
            arrow
        >
            <TextField
                {...textFieldProps}
                label={label}
                value={value}
                onChange={handleChange}
                onBlur={handleBlur}
                error={helperText ? showError : error}
                helperText={displayHelper}
                placeholder="HH:MM"
                slotProps={{
                    htmlInput: {
                        'aria-label': label,
                        'style': { fontVariantNumeric: 'tabular-nums', letterSpacing: '0.08em' },
                    },
                    input: {
                        inputComponent: DurationMaskAdapter as any,
                        startAdornment: (
                            <InputAdornment position="start">
                                <AccessTimeIcon
                                    fontSize="small"
                                    color={showError ? 'error' : 'action'}
                                    sx={{ transition: 'color 0.2s' }}
                                />
                            </InputAdornment>
                        )
                    }

                }}
            />
        </Tooltip>
    );
};

